import {
    Card,
    Icon,
    Tag,
    Typography,
    Checkbox,
    ButtonMenu,
    Alert,
} from "@ann/web-components";
import {
    CardWrapper,
    CardTop,
    CardHeader,
    CardHeaderTitle,
    CardMeta,
    CardMetaItem,
} from "./Card.styled";
import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import { observer } from "mobx-react";
import { ModalBase } from "../../../../components/Modal";
import { useStore } from "../../../../../../../hooks";

//  PERFORMANCE: observer for MobX reactivity, memo for performance
const DocumentCard = observer(({ uuid, name, state, selectMode, onSelect, selectedItems, onRemoveAction, onDeleteSuccess, setShowSlideoutDocumentDetail, setSelectedDocument }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [alert, setAlert] = useState(null);
    const { documentStore, playgroundConfigStore } = useStore();

    //  PERFORMANCE: Memoized selection check
    const isSelected = useMemo(() => {
        return selectedItems.includes(uuid);
    }, [selectedItems, uuid]);

    //  PERFORMANCE: Memoized callbacks
    const handleDelete = useCallback(async () => {
        try {
            const agentUuid = playgroundConfigStore.agentStore.uuid;
            const tenantId = "58145bef-4f71-468a-bded-1823d2a08f58";

            if (!agentUuid) {
                throw new Error('Agent UUID not found');
            }

            const result = await documentStore.deleteDocument(uuid, agentUuid, tenantId);

            if (result.success) {
                setAlert({
                    title: "Success",
                    message: `Document "${name}" deleted successfully`,
                    severity: "success"
                });

                // Notify parent component to remove from list
                if (onDeleteSuccess) {
                    onDeleteSuccess(uuid);
                }
            } else {
                throw new Error(result.error || 'Failed to delete document');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Error deleting document';
            setAlert({
                title: "Error",
                message: errorMessage,
                severity: "error"
            });
        } finally {
            setShowDeleteModal(false);
        }
    }, [uuid, name, documentStore, playgroundConfigStore, onDeleteSuccess]);

    const handleDeleteClick = useCallback(() => {
        if (onRemoveAction) {
            onRemoveAction();
        }
        setShowDeleteModal(true);
    }, [onRemoveAction]);

    const handleCloseAlert = useCallback(() => {
        setAlert(null);
    }, []);

    const handleCheckboxChange = useCallback(() => {
        onSelect(uuid);
    }, [onSelect, uuid]);

    const handleMenuChange = useCallback((value) => {
        if (value === 'delete') {
            handleDeleteClick();
        } else if (value === 'view-details') {
            setSelectedDocument({
                uuid,
                title: name
            });
            setShowSlideoutDocumentDetail(true);
        }
    }, [handleDeleteClick, uuid, name, setSelectedDocument, setShowSlideoutDocumentDetail]);

    return (
        <>
            {alert && (
                <Alert
                    title={alert.message}
                    severity={alert.severity}
                    onClose={handleCloseAlert}
                    maxWidth="400px"
                    closable={true}
                    autoClose={true}
                    autoCloseDelay={3000}
                />
            )}
            <Card disabled={state === "loading"}>
                <CardWrapper>
                    <CardTop>
                        <CardHeader>
                            {selectMode && (
                                <Checkbox
                                    onChange={handleCheckboxChange}
                                    size="medium"
                                    checked={isSelected}
                                />
                            )}
                            <Icon name="description" size="medium" />
                            <CardHeaderTitle>
                                <Typography category="subtitle" size="medium" weight="bold" color="title">
                                    {name}
                                </Typography>
                            </CardHeaderTitle>
                            {state === "loading" && (
                                <Tag icon="info" size="small" color="informative" text="Processing" spinner={true} />
                            )}
                            <ButtonMenu
                                customIcon="dotsMenu"
                                disableEffects
                                iconPosition="right"
                                label=""
                                menuPosition="bottom"
                                onChange={handleMenuChange}
                                size="medium"
                                type="tertiary"
                                width="260px"
                            >
                                <ButtonMenu.Menu>
                                    <ButtonMenu.Item
                                        label="Delete"
                                        value="delete"
                                    />
                                    <ButtonMenu.Item
                                        label="View details"
                                        value="view-details"
                                    />
                                </ButtonMenu.Menu>
                            </ButtonMenu>
                        </CardHeader>

                        <Typography
                            category="body"
                            size="medium"
                            weight="regular"
                            color="body"
                        >
                            Example description of the content that could go inside.
                        </Typography>
                    </CardTop>

                    <CardMeta>
                        <CardMetaItem>
                            <Tag icon="person" size="medium" color="informative" />
                            <Typography category="body" size="small" weight="regular" color="subtitle">
                                jane doe
                            </Typography>
                        </CardMetaItem>

                        <CardMetaItem>
                            <Tag icon="calendarToday" size="medium" color="informative" />
                            <Typography category="body" size="small" weight="regular" color="subtitle">
                                2025-08-10
                            </Typography>
                        </CardMetaItem>
                    </CardMeta>
                </CardWrapper>
            </Card>
            {showDeleteModal && (
                <ModalBase
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onClick={handleDelete}
                    title={`Are you sure you want to delete this file?`}
                    description={"This action will unassign the file from your agent's knowledge base and soft delete it."}
                    buttonTitle={documentStore.isDeletingDocument ? "Deleting..." : "Delete"}
                    disabled={documentStore.isDeletingDocument}
                    modalWidth="571px" />
            )}
        </>
    )
});

DocumentCard.displayName = 'DocumentCard';

export default memo(DocumentCard);
